import { Type, Static } from '@sinclair/typebox';
import { EmailFileUploadSchema } from '../../shared/file/file-request.schema';
import { IsoDateTimeSchema } from '../../shared/utils/date-time.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';
import { EmailAttendeeSchema } from './email.schema';

const emailsListSearchModeNote =
  'Activates search mode: results may be approximate and non-exhaustive (provider-dependent, ~1000 results on Outlook).';

const outlookParticipantFilterNote =
  'For Outlook, some messages matching this filter may be missing due to a provider limitation. Use `GET /threads/:thread_id` to retrieve a specific conversation.';

const emailsListParticipantFiltersSchema = Type.Object({
  to: Type.Optional(
    Type.String({
      description: `Return messages sent to the given email address. ${outlookParticipantFilterNote} ${emailsListSearchModeNote}`,
    }),
  ),
  from: Type.Optional(
    Type.String({
      description: `Return messages sent from the given email address. ${outlookParticipantFilterNote} ${emailsListSearchModeNote}`,
    }),
  ),
  any_email: Type.Optional(
    Type.String({
      description: `Return messages where at least one of the given comma-separated email addresses appears as a participant (from, to, cc, or bcc). ${outlookParticipantFilterNote} ${emailsListSearchModeNote}`,
    }),
  ),
});

const emailsListStatusFiltersSchema = Type.Object({
  has_attachment: Type.Optional(
    Type.Boolean({
      description:
        'Filter to only return emails with or without attachments. For Outlook with `meta_only=true`, this uses the provider attachment flag, which excludes inline attachments.',
    }),
  ),
  is_starred: Type.Optional(
    Type.Boolean({
      description: 'Filter to only return starred or unstarred emails.',
    }),
  ),
  is_unread: Type.Optional(
    Type.Boolean({
      description: 'Filter to only return unread or read emails.',
    }),
  ),
});

const emailsListScopeAndTimeFiltersSchema = Type.Object({
  before: Type.Optional(
    IsoDateTimeSchema({
      description: 'Filter to only return emails sent before the given datetime (exclusive).',
    }),
  ),
  exclude_folder: Type.Optional(
    Type.String({
      description:
        'Filter to only return emails that are not in any of the folders specified in the comma-separated list of folder IDs.',
    }),
  ),
  after: Type.Optional(
    IsoDateTimeSchema({
      description: 'Filter to only return emails sent after the given datetime (exclusive).',
    }),
  ),
});

export const GetEmailsListQuerySchema = Type.Intersect([
  Type.Object({
    q: Type.Optional(
      Type.String({
        description: `Full-text search in the email subject and body (case-insensitive, partial match). ${emailsListSearchModeNote} Combine with \`from\`, \`to\`, or \`any_email\` to narrow by participant address.`,
      }),
    ),
    meta_only: Type.Optional(
      Type.Boolean({
        default: false,
        description:
          'Speed up the response by only returning the email metadata. body and body_plain are empty strings, and attachments metadata is excluded.',
      }),
    ),
  }),
  emailsListParticipantFiltersSchema,
  emailsListStatusFiltersSchema,
  emailsListScopeAndTimeFiltersSchema,
  PaginationQueryParamsSchema,
]);

export const GetEmailParamsSchema = Type.Object({
  email_id: Type.String({ minLength: 1, description: 'ID of the Email to retrieve.' }),
});

export const GetFolderEmailsListQuerySchema = GetEmailsListQuerySchema;
export const GetFolderEmailsListParamsSchema = Type.Object({
  folder_id: Type.String({ minLength: 1, description: 'ID of the Folder to retrieve emails from.' }),
});

export const GetThreadParamsSchema = Type.Object({
  thread_id: Type.String({
    minLength: 1,
    description:
      'ID of the thread to retrieve. For IMAP, use the thread_id returned by an email; results based on header-derived IDs may be incomplete.',
  }),
});
export const GetThreadQuerySchema = Type.Object({
  meta_only: Type.Optional(
    Type.Boolean({
      default: false,
      description:
        'Speed up the response by only returning the email metadata. body and body_plain are empty strings, and attachments metadata is excluded.',
    }),
  ),
});

export const GetEmailQuerySchema = Type.Object({
  meta_only: Type.Optional(
    Type.Boolean({
      default: false,
      description:
        'Speed up the response by only returning the email metadata. body and body_plain are empty strings, and attachments metadata is excluded.',
    }),
  ),
});

export const GetEmailAttachmentParamsSchema = Type.Object({
  email_id: Type.String({ minLength: 1, description: 'ID of the parent Email.' }),
  attachment_id: Type.String({ minLength: 1, description: 'ID of the Email Attachment to retrieve.' }),
});

export const TrashEmailParamsSchema = Type.Object({
  email_id: Type.String({ minLength: 1, description: 'ID of the Email to trash.' }),
});

export const ModifyEmailParamsSchema = Type.Object({
  email_id: Type.String({ minLength: 1, description: 'ID of the Email to modify.' }),
});

export const ModifyEmailBaseBodySchema = Type.Object(
  {
    folders_ids: Type.Optional(
      Type.Array(Type.String({ minLength: 1 }), {
        minItems: 1,
        description:
          'The ID(s) of the folder(s) to apply, overwriting all folders previously associated with the Email. Outlook emails can be in a single folder only. Google allows a single email to appear in multiple folders. For Gmail, SENT is ignored: this operation cannot add or remove it, and its existing membership is preserved whether SENT is included or omitted. UNREAD is also ignored and read status is preserved; use the mark-as-read or mark-as-unread operation to change it.',
      }),
    ),
    specifics: Type.Optional(Type.Any()),
  },
  { additionalProperties: false },
);

export const UnreadEmailParamsSchema = Type.Object({
  email_id: Type.String({ minLength: 1, description: 'ID of the Email to mark as unread.' }),
});

export const ReadEmailParamsSchema = Type.Object({
  email_id: Type.String({ minLength: 1, description: 'ID of the Email to mark as read.' }),
});

export const TrackingOptionsSchema = Type.Object(
  {
    opens: Type.Optional(
      Type.Boolean({
        default: false,
        description:
          'If `true`, trigger webhooks listening to `tracking.open` when the email is opened by the recipient. Opens rely on a tracking pixel, which mail clients, privacy proxies and security scanners can also load without the recipient reading the email. Each event carries `potentially_bot` and `bot_reasons` so you can exclude these automated opens.',
      }),
    ),
    clicks: Type.Optional(
      Type.Boolean({
        default: false,
        description:
          'If `true`, trigger webhooks listening to `tracking.click` when the recipient click on a link in the email content.',
      }),
    ),
    label: Type.Optional(
      Type.String({
        description: 'A label to be sent in the tracking event. This can help you to identify the sent email.',
      }),
    ),
    custom_domain: Type.Optional(
      Type.String({
        description:
          'A custom domain verified for the organization, enabled for the application, and pointing to https://track.lnk-fllw.com.',
      }),
    ),
  },
  {
    description: 'Configuration of tracking for this email.',
    additionalProperties: false,
  },
);

export const SendEmailBodySchema = Type.Object(
  {
    html: Type.Optional(Type.String({ description: 'The HTML content of the email. Required for tracking.' })),
    plain_text: Type.Optional(Type.String({ description: 'The plain text content of the email.' })),
    subject: Type.Optional(Type.String({ description: 'Subject of the email.' })),
    from: Type.Optional(EmailAttendeeSchema),
    to: Type.Array(EmailAttendeeSchema, { description: 'List of attendees to set in `to` header.' }),
    cc: Type.Optional(Type.Array(EmailAttendeeSchema, { description: 'List of attendees to set in `cc` header.' })),
    bcc: Type.Optional(Type.Array(EmailAttendeeSchema, { description: 'List of attendees to set in `bcc` header.' })),
    reply_to: Type.Optional(
      Type.Array(EmailAttendeeSchema, {
        description:
          'List of attendees to set in the standard `Reply-To` header. Recipients answering the email will reply to these addresses instead of `from`. To reply inside an existing conversation, use `reply_to_provider_id` instead.',
      }),
    ),
    reply_to_provider_id: Type.Optional(
      Type.String({
        minLength: 1,
        description:
          'The provider ID of the email to reply to. Use the `id` returned when listing or retrieving the original email. Takes precedence over the deprecated `reply_to_message_id` field when both are provided.',
      }),
    ),
    reply_to_message_id: Type.Optional(
      Type.String({
        minLength: 1,
        deprecated: true,
        description: `Deprecated: use reply_to_provider_id instead. The ID of the email message that you're replying to. For Gmail and Outlook, this is the provider ID. For IMAP Send, this is the RFC822 Message-ID header and does not mark the original email as answered. Ignored when reply_to_provider_id is provided.`,
      }),
    ),
    attachments: Type.Optional(
      Type.Array(EmailFileUploadSchema, {
        description: 'The list of file attachments to the email.',
      }),
    ),
    custom_headers: Type.Optional(
      Type.Array(
        Type.Object({
          name: Type.String(),
          value: Type.String(),
        }),
        {
          description:
            'An array of custom headers to add to the email. Each header overrides any existing header with the same name (case-insensitive), including headers set by other fields such as `to`, `cc`, `bcc`, `from`, `subject`, or `reply_to`. A `Reply-To` custom header therefore takes precedence over the `reply_to` field and its value is used verbatim.',
        },
      ),
    ),
    tracking_options: Type.Optional(TrackingOptionsSchema),
  },
  { additionalProperties: false },
);

export type GetEmailsListQuery = Static<typeof GetEmailsListQuerySchema>;
export type GetFolderEmailsListQuery = Static<typeof GetFolderEmailsListQuerySchema>;
export type GetFolderEmailsListParams = Static<typeof GetFolderEmailsListParamsSchema>;
export type GetThreadParams = Static<typeof GetThreadParamsSchema>;
export type GetThreadQuery = Static<typeof GetThreadQuerySchema>;
export type GetEmailParams = Static<typeof GetEmailParamsSchema>;
export type GetEmailQuery = Static<typeof GetEmailQuerySchema>;
export type GetEmailAttachmentParams = Static<typeof GetEmailAttachmentParamsSchema>;
export type TrashEmailParams = Static<typeof TrashEmailParamsSchema>;
export type ModifyEmailParams = Static<typeof ModifyEmailParamsSchema>;
export type ModifyEmailBaseBody<Specifics = object> = Omit<Static<typeof ModifyEmailBaseBodySchema>, 'specifics'> & {
  specifics?: Specifics;
};
export type UnreadEmailParams = Static<typeof UnreadEmailParamsSchema>;
export type ReadEmailParams = Static<typeof ReadEmailParamsSchema>;
export type SendEmailBody = Static<typeof SendEmailBodySchema>;
export type TrackingOptions = SendEmailBody['tracking_options'];
